
import React, { useState, useEffect } from 'react';
import RichTextEditor from '../../components/base/RichTextEditor';
import { supabase, Article, articleToDB, articleFromDB } from '../../lib/supabase';

interface Article {
  id: string;
  title: string;
  summary: string;
  content: string;
  date: string;
  columnType: 'industry' | 'country'; // 栏目类型
  category: string; // 具体分类
  link: string;
  image: string;
  status: 'draft' | 'published';
}

// 登录组件
const LoginForm: React.FC<{ onLogin: () => void }> = ({ onLogin }) => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleLogin = (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    // 模拟登录验证（您可以根据需要修改用户名和密码）
    const validUsername = 'admin';
    const validPassword = 'admin@2025';

    setTimeout(() => {
      if (username === validUsername && password === validPassword) {
        localStorage.setItem('adi_admin_logged_in', 'true');
        localStorage.setItem('adi_admin_login_time', Date.now().toString());
        onLogin();
      } else {
        setError('用户名或密码错误');
      }
      setIsLoading(false);
    }, 500);
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          <img
            alt="ADI Logo"
            width={120}
            height={54}
            src="https://static.readdy.ai/image/4d7c1148f372075ad366b96364633fcd/3900b388860a5efa532a02080a385ec7.png"
            className="mx-auto h-12 w-auto"
          />
          <h2 className="mt-6 text-3xl font-bold text-gray-900">ADI亚数智库</h2>
          <p className="mt-2 text-sm text-gray-600">内容管理后台登录</p>
        </div>
        <form className="mt-8 space-y-6" onSubmit={handleLogin}>
          <div className="bg-white p-8 rounded-lg shadow-sm space-y-6">
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
                <div className="flex items-center">
                  <i className="ri-error-warning-line mr-2"></i>
                  {error}
                </div>
              </div>
            )}

            <div>
              <label htmlFor="username" className="block text-sm font-medium text-gray-700 mb-2">
                用户名
              </label>
              <input
                id="username"
                name="username"
                type="text"
                required
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="请输入用户名"
              />
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                密码
              </label>
              <input
                id="password"
                name="password"
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="请输入密码"
              />
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className="w-full flex justify-center py-2 px-4 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? (
                <div className="flex items-center">
                  <i className="ri-loader-4-line animate-spin mr-2"></i>
                  登录中...
                </div>
              ) : (
                '登录'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const AdminPanel: React.FC = () => {
  const [articles, setArticles] = useState<Article[]>([]);
  const [isEditing, setIsEditing] = useState(false);
  const [currentArticle, setCurrentArticle] = useState<Article | null>(null);
  const [showEditor, setShowEditor] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const industryCategories = [
    '数字经济',
    '金融科技',
    '电子商务',
    '制造业',
    '新能源',
    '医疗健康',
    '基础设施',
  ];
  const countryCategories = [
    '印度尼西亚',
    '马来西亚',
    '泰国',
    '菲律宾',
    '越南',
    '新加坡',
    '其他东南亚国家',
  ];

  // 检查登录状态
  useEffect(() => {
    const checkLoginStatus = () => {
      const loggedIn = localStorage.getItem('adi_admin_logged_in');
      const loginTime = localStorage.getItem('adi_admin_login_time');

      if (loggedIn === 'true' && loginTime) {
        const currentTime = Date.now();
        const loginTimestamp = parseInt(loginTime);
        const sessionDuration = 24 * 60 * 60 * 1000; // 24小时

        if (currentTime - loginTimestamp < sessionDuration) {
          setIsLoggedIn(true);
        } else {
          // 会话过期，清除登录状态
          localStorage.removeItem('adi_admin_logged_in');
          localStorage.removeItem('adi_admin_login_time');
          setIsLoggedIn(false);
        }
      }
    };

    checkLoginStatus();
  }, []);

  // 从Supabase加载文章
  useEffect(() => {
    if (isLoggedIn) {
      loadArticles();
    }
  }, [isLoggedIn]);

  const loadArticles = async () => {
    setIsLoading(true);
    try {
      const { data, error } = await supabase
        .from('articles')
        .select('*')
        .order('date', { ascending: false });

      if (error) throw error;

      if (data) {
        // 转换数据库格式到前端格式
        const articlesFormatted = data.map(articleFromDB);
        setArticles(articlesFormatted);
      }
    } catch (error) {
      console.error('加载文章失败:', error);
      alert('加载文章失败，请刷新页面重试');
    } finally {
      setIsLoading(false);
    }
  };

  // 登出功能
  const handleLogout = () => {
    localStorage.removeItem('adi_admin_logged_in');
    localStorage.removeItem('adi_admin_login_time');
    setIsLoggedIn(false);
    setShowEditor(false);
    setCurrentArticle(null);
  };

  // 如果未登录，显示登录表单
  if (!isLoggedIn) {
    return <LoginForm onLogin={() => setIsLoggedIn(true)} />;
  }

  // 保存文章到Supabase
  const saveArticles = async (article: Article, isNew: boolean) => {
    setIsLoading(true);
    try {
      if (isNew) {
        // 新建文章 - 不传入id，让数据库自动生成
        const articleData = articleToDB(article);
        const { data, error } = await supabase
          .from('articles')
          .insert([articleData])
          .select();

        if (error) throw error;

        if (data) {
          await loadArticles();
          alert('文章创建成功！');
        }
      } else {
        // 更新文章
        const articleData = articleToDB(article);
        const { error } = await supabase
          .from('articles')
          .update(articleData)
          .eq('id', article.id);

        if (error) throw error;

        await loadArticles();
        alert('文章更新成功！');
      }
    } catch (error) {
      console.error('保存文章失败:', error);
      alert(`保存文章失败：${error.message || '请重试'}`);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const handleNewArticle = () => {
    const newArticle: Article = {
      // 不设置id，让数据库自动生成
      title: '',
      summary: '',
      content: '',
      date: new Date().toISOString().split('T')[0],
      columnType: 'industry',
      category: industryCategories[0],
      link: '',
      image: '',
      status: 'draft',
    };
    setCurrentArticle(newArticle);
    setIsEditing(false);
    setShowEditor(true);
  };

  const handleEditArticle = (article: Article) => {
    setCurrentArticle(article);
    setIsEditing(true);
    setShowEditor(true);
  };

  const handleSaveArticle = async () => {
    if (!currentArticle) return;

    try {
      await saveArticles(currentArticle, !isEditing);
      setShowEditor(false);
      setCurrentArticle(null);
    } catch (error) {
      // 错误已在saveArticles中处理
    }
  };

  const handleDeleteArticle = async (id: string) => {
    if (!confirm('确定要删除这篇文章吗？')) return;

    setIsLoading(true);
    try {
      const { error } = await supabase
        .from('articles')
        .delete()
        .eq('id', id);

      if (error) throw error;

      await loadArticles();
      alert('文章删除成功！');
    } catch (error) {
      console.error('删除文章失败:', error);
      alert(`删除文章失败：${error.message || '请重试'}`);
    } finally {
      setIsLoading(false);
    }
  };

  const handlePublishArticle = async (id: string) => {
    const article = articles.find((a) => a.id === id);
    if (!article) return;

    const newStatus = article.status === 'published' ? 'draft' : 'published';

    setIsLoading(true);
    try {
      const { error } = await supabase
        .from('articles')
        .update({ status: newStatus })
        .eq('id', id);

      if (error) throw error;

      await loadArticles();
      alert(newStatus === 'published' ? '文章已发布！' : '文章已取消发布！');
    } catch (error) {
      console.error('更新文章状态失败:', error);
      alert(`更新文章状态失败：${error.message || '请重试'}`);
    } finally {
      setIsLoading(false);
    }
  };

  if (showEditor) {
    return (
      <ArticleEditor
        article={currentArticle}
        setArticle={setCurrentArticle as any}
        onSave={handleSaveArticle}
        onCancel={() => setShowEditor(false)}
        industryCategories={industryCategories}
        countryCategories={countryCategories}
        isEditing={isEditing}
        onLogout={handleLogout}
      />
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <img
                alt="ADI Logo"
                width={100}
                height={45}
                src="https://static.readdy.ai/image/4d7c1148f372075ad366b96364633fcd/3900b388860a5efa532a02080a385ec7.png"
                className="h-10 w-auto"
              />
              <h1 className="text-2xl font-bold text-gray-900">
                ADI亚数智库 - 内容管理后台
              </h1>
            </div>
            <div className="flex items-center space-x-4">
              <a
                href="/zh/news"
                target="_blank"
                className="text-blue-600 hover:text-blue-700 flex items-center"
              >
                <i className="ri-external-link-line mr-1"></i>
                查看前台
              </a>
              <button
                onClick={handleNewArticle}
                disabled={isLoading}
                className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <i className="ri-add-line mr-2"></i>
                新建文章
              </button>
              <button
                onClick={handleLogout}
                className="text-gray-600 hover:text-gray-700 px-4 py-2 rounded-lg border flex items-center"
              >
                <i className="ri-logout-box-line mr-2"></i>
                退出登录
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 py-8">
        <div className="bg-white rounded-lg shadow-sm">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">文章管理</h2>
            <p className="text-sm text-gray-600 mt-1">
              管理ADI亚数智库的所有文章内容
            </p>
          </div>

          {isLoading && (
            <div className="px-6 py-4 bg-blue-50 border-b border-blue-100">
              <div className="flex items-center text-blue-600">
                <i className="ri-loader-4-line animate-spin mr-2"></i>
                加载中...
              </div>
            </div>
          )}

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    标题
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    栏目类型
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    分类
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    状态
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    发布日期
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    操作
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {articles.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-6 py-12 text-center text-gray-500">
                      <div className="flex flex-col items-center">
                        <i className="ri-file-text-line text-4xl text-gray-300 mb-4"></i>
                        <p className="text-lg font-medium mb-2">暂无文章</p>
                        <p className="text-sm">点击"新建文章"开始创建您的第一篇文章</p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  articles.map((article) => (
                    <tr key={article.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4">
                        <div className="max-w-xs">
                          <p className="text-sm font-medium text-gray-900 truncate">
                            {article.title || '未命名文章'}
                          </p>
                          <p className="text-sm text-gray-500 truncate">{article.summary}</p>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span
                          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            article.columnType === 'industry'
                              ? 'bg-purple-100 text-purple-800'
                              : 'bg-orange-100 text-orange-800'
                          }`}
                        >
                          {article.columnType === 'industry' ? '行业栏目' : '国家栏目'}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <span
                          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            article.columnType === 'industry'
                              ? 'bg-blue-100 text-blue-800'
                              : 'bg-green-100 text-green-800'
                          }`}
                        >
                          {article.category}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <span
                          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            article.status === 'published'
                              ? 'bg-green-100 text-green-800'
                              : 'bg-yellow-100 text-yellow-800'
                          }`}
                        >
                          {article.status === 'published' ? '已发布' : '草稿'}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500">{article.date}</td>
                      <td className="px-6 py-4">
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={() => handleEditArticle(article)}
                            disabled={isLoading}
                            className="text-blue-600 hover:text-blue-700 p-1 disabled:opacity-50"
                            title="编辑"
                          >
                            <i className="ri-edit-line"></i>
                          </button>
                          <button
                            onClick={() => handlePublishArticle(article.id)}
                            disabled={isLoading}
                            className={`p-1 disabled:opacity-50 ${
                              article.status === 'published'
                                ? 'text-yellow-600 hover:text-yellow-700'
                                : 'text-green-600 hover:text-green-700'
                            }`}
                            title={article.status === 'published' ? '取消发布' : '发布'}
                          >
                            <i
                              className={
                                article.status === 'published' ? 'ri-eye-off-line' : 'ri-eye-line'
                              }
                            ></i>
                          </button>
                          <button
                            onClick={() => handleDeleteArticle(article.id)}
                            disabled={isLoading}
                            className="text-red-600 hover:text-red-700 p-1 disabled:opacity-50"
                            title="删除"
                          >
                            <i className="ri-delete-bin-line"></i>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </main>
    </div>
  );
};

interface ArticleEditorProps {
  article: Article | null;
  setArticle: (article: Article) => void;
  onSave: () => void;
  onCancel: () => void;
  industryCategories: string[];
  countryCategories: string[];
  isEditing: boolean;
  onLogout: () => void;
}

const ArticleEditor: React.FC<ArticleEditorProps> = ({
  article,
  setArticle,
  onSave,
  onCancel,
  industryCategories,
  countryCategories,
  isEditing,
  onLogout,
}) => {
  const [activeTab, setActiveTab] = useState<'edit' | 'preview'>('edit');
  const [isExtracting, setIsExtracting] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [coverImageUploading, setCoverImageUploading] = useState(false);

  if (!article) return null;

  const handleInputChange = (field: keyof Article, value: string) => {
    setArticle({ ...article, [field]: value });
  };

  const handleColumnTypeChange = (columnType: 'industry' | 'country') => {
    const categories = columnType === 'industry' ? industryCategories : countryCategories;
    setArticle({
      ...article,
      columnType,
      category: categories[0],
    });
  };

  // 封面图片上传处理
  const handleCoverImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      alert('请选择图片文件');
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      alert('图片文件大小不能超过10MB');
      return;
    }

    setCoverImageUploading(true);

    const reader = new FileReader();
    reader.onload = (e) => {
      const base64 = e.target?.result as string;
      setArticle({ ...article, image: base64 });
      setCoverImageUploading(false);
    };
    reader.onerror = () => {
      alert('图片上传失败，请重试');
      setCoverImageUploading(false);
    };
    reader.readAsDataURL(file);

    // 清空input值，允许重复选择同一文件
    event.target.value = '';
  };

  const triggerCoverImageUpload = () => {
    const input = document.getElementById('cover-image-upload') as HTMLInputElement;
    input?.click();
  };

  // 智能内容处理功能
  const processContentIntelligently = async () => {
    if (!article.content.trim()) {
      alert('请先在内容编辑器中输入内容');
      return;
    }

    setIsProcessing(true);

    try {
      // 模拟智能处理过程
      await new Promise((resolve) => setTimeout(resolve, 2000));

      let processedContent = article.content;
      let generatedTitle = article.title;
      let generatedSummary = article.summary;
      let generatedImages: string[] = [];

      // 1. 自动生成标题（若为空）
      if (!generatedTitle.trim()) {
        const textContent = processedContent.replace(/<[^>]*>/g, '').trim();
        const lines = textContent.split('\n').filter((line) => line.trim());
        if (lines.length > 0) {
          generatedTitle = lines[0].trim();
          if (generatedTitle.length > 100) {
            const shortLines = lines.filter(
              (line) => line.length < 100 && line.length > 10,
            );
            generatedTitle = shortLines.length
              ? shortLines[0].trim()
              : generatedTitle.substring(0, 80) + '...';
          }
        }
      }

      // 2. 自动生成摘要（若为空）
      if (!generatedSummary.trim()) {
        const cleanContent = processedContent
          .replace(/<[^>]*>/g, '')
          .replace(/\n+/g, ' ')
          .trim();

        generatedSummary = cleanContent.substring(0, 200).trim();
        if (cleanContent.length > 200) {
          const lastPeriod = generatedSummary.lastIndexOf('。');
          generatedSummary =
            lastPeriod > 100
              ? generatedSummary.substring(0, lastPeriod + 1)
              : generatedSummary + '...';
        }
      }

      // 3. 自动生成配图
      const keywords = extractKeywords(processedContent);
      if (keywords.length > 0) {
        const imagePrompts = generateImagePrompts(
          keywords,
          article.columnType,
          article.category,
        );
        generatedImages = imagePrompts.map((prompt, index) => {
          const encodedPrompt = encodeURIComponent(prompt);
          const timestamp = Date.now();
          const uniqueId = `img${timestamp}${index}`;
          return `https://public.readdy.ai/ai/img_res/948a13f1eb402f8ff16472e217608fb6.jpg$%7BencodedPrompt%7D&width=800&height=400&seq=${uniqueId}&orientation=landscape`;
        });
      }

      // 更新文章
      setArticle({
        ...article,
        title: generatedTitle || article.title,
        content: processedContent,
        summary: generatedSummary || article.summary,
        image: generatedImages[0] || article.image,
      });

      alert(
        '智能处理完成！已自动优化文章格式、生成标题摘要并添加配图。请检查并完善内容。',
      );
    } catch (error) {
      console.error('智能处理失败:', error);
      alert('智能处理失败，请稍后重试。');
    } finally {
      setIsProcessing(false);
    }
  };

  // 提取关键词辅助函数
  const extractKeywords = (content: string): string[] => {
    const cleanContent = content.replace(/<[^>]*>/g, '').toLowerCase();
    const keywords: string[] = [];

    if (article.columnType === 'industry') {
      const industryKeywords = [
        '数字经济',
        '制造业',
        '金融科技',
        '电子商务',
        '新能源',
        '医疗健康',
        '基础设施',
      ];
      industryKeywords.forEach((keyword) => {
        if (cleanContent.includes(keyword.toLowerCase())) {
          keywords.push(keyword);
        }
      });
    } else {
      const countryKeywords = [
        '印度尼西亚',
        '马来西亚',
        '泰国',
        '菲律宾',
        '越南',
        '新加坡',
      ];
      countryKeywords.forEach((keyword) => {
        if (cleanContent.includes(keyword)) {
          keywords.push(keyword);
        }
      });
    }

    const commonKeywords = [
      '发展',
      '增长',
      '投资',
      '市场',
      '经济',
      '技术',
      '创新',
      '数据',
      '报告',
      '分析',
    ];
    commonKeywords.forEach((keyword) => {
      if (cleanContent.includes(keyword) && !keywords.includes(keyword)) {
        keywords.push(keyword);
      }
    });

    return keywords.slice(0, 4);
  };

  // 生成图片提示词辅助函数
  const generateImagePrompts = (
    keywords: string[],
    columnType: string,
    category: string,
  ): string[] => {
    const prompts: string[] = [];

    if (columnType === 'industry') {
      keywords.forEach((keyword) => {
        switch (keyword) {
          case '数字经济':
            prompts.push(
              'digital economy technology business modern office with computers and data visualization charts, professional business environment, clean minimalist background',
            );
            break;
          case '制造业':
            prompts.push(
              'smart manufacturing industry automation robots and machinery in modern factory, industrial technology, clean professional background',
            );
            break;
          case '金融科技':
            prompts.push(
              'fintech financial technology innovation digital banking mobile payment apps, modern business graphics, clean background',
            );
            break;
          case '电子商务':
            prompts.push(
              'ecommerce online shopping digital marketplace with shopping cart icons and mobile devices, modern business concept, clean background',
            );
            break;
          case '新能源':
            prompts.push(
              'renewable energy solar panels and wind turbines in clean environment, sustainable technology, bright clean background',
            );
            break;
          case '医疗健康':
            prompts.push(
              'healthcare medical technology digital health monitoring devices and medical charts, modern healthcare concept, clean background',
            );
            break;
          case '基础设施':
            prompts.push(
              'infrastructure development modern construction and transportation systems, urban development, clean professional background',
            );
            break;
          default:
            prompts.push(
              `${keyword} business technology modern professional concept with clean minimalist background`,
            );
        }
      });
    } else {
      const countryPrompts: Record<string, string> = {
        印度尼西亚:
          'Indonesia business development modern cityscape with Indonesian architecture elements, professional business concept, clean background',
        马来西亚:
          'Malaysia economic growth modern business district with Malaysian landmarks, professional development concept, clean background',
        泰国:
          'Thailand business market modern Bangkok skyline with business elements, economic development concept, clean background',
        菲律宾:
          'Philippines economic development modern Manila business district, professional growth concept, clean background',
        越南:
          'Vietnam business growth modern Ho Chi Minh City business area, economic development concept, clean background',
        新加坡:
          'Singapore business innovation modern Marina Bay business district, professional technology concept, clean background',
      };

      prompts.push(countryPrompts[category] || `${category} business development modern professional concept with clean background`);

      if (keywords.length > 0) {
        prompts.push(`${keywords[0]} ${category} industry development modern business concept with clean professional background`);
      }
    }

    return prompts.slice(0, 3);
  };

  // 智能内容抓取功能
  const extractContentFromUrl = async () => {
    if (!article.link.trim()) {
      alert('请先输入文章链接');
      return;
    }

    setIsExtracting(true);

    try {
      const response = await fetch(
        `https://api.allorigins.win/get?url=${encodeURIComponent(article.link)}`,
      );
      const data = await response.json();

      if (data.contents) {
        const parser = new DOMParser();
        const doc = parser.parseFromString(data.contents, 'text/html');

        // 提取标题
        let extractedTitle = '';
        const titleSelectors = [
          'h1',
          '.rich_media_title',
          '[data-role="title"]',
          'title',
          '.article-title',
          '.post-title',
        ];
        for (const selector of titleSelectors) {
          const el = doc.querySelector(selector);
          if (el && el.textContent?.trim()) {
            extractedTitle = el.textContent.trim();
            break;
          }
        }

        // 提取内容
        let extractedContent = '';
        const contentSelectors = [
          '.rich_media_content',
          '[data-role="content"]',
          '.article-content',
          '.post-content',
          '.content',
          'article',
          '.entry-content',
        ];
        for (const selector of contentSelectors) {
          const el = doc.querySelector(selector);
          if (el) {
            extractedContent = el.innerHTML;
            break;
          }
        }

        // 提取封面图片
        let extractedImage = '';
        const imageSelectors = [
          '.rich_media_thumb',
          '[data-role="cover"]',
          '.article-cover img',
          '.post-thumbnail img',
          'article img:first-of-type',
        ];
        for (const selector of imageSelectors) {
          const el = doc.querySelector(selector);
          if (el && el.getAttribute('src')) {
            extractedImage = el.getAttribute('src') || '';
            if (extractedImage.startsWith('//')) {
              extractedImage = 'https:' + extractedImage;
            } else if (extractedImage.startsWith('/')) {
              const urlObj = new URL(article.link);
              extractedImage = urlObj.origin + extractedImage;
            }
            break;
          }
        }

        // 生成摘要（取前200字符）
        const extractedSummary =
          extractedContent
            .replace(/<[^>]*>/g, '')
            .substring(0, 200)
            .trim() + (extractedContent.length > 200 ? '...' : '');

        // 更新文章
        setArticle({
          ...article,
          title: extractedTitle || article.title,
          content: extractedContent || article.content,
          summary: extractedSummary || article.summary,
          image: extractedImage || article.image,
        });

        alert('内容抓取成功！请检查并完善抓取的内容。');
      } else {
        throw new Error('无法获取页面内容');
      }
    } catch (error) {
      console.error('内容抓取失败:', error);
      const manual = confirm(
        '自动抓取失败，是否手动输入内容？\n\n可能原因：\n1. 网站有防爬虫保护\n2. 需要登录访问\n3. 网络连接问题\n\n点击"确定"继续手动编辑，点击"取消"重试抓取。',
      );
      if (!manual) {
        alert(
          '请检查链接是否正确，或稍后重试。\n提示：\n- 确保链接可正常访问\n- 某些网站可能需要特殊处理\n- 您也可以手动复制粘贴内容',
        );
      }
    } finally {
      setIsExtracting(false);
    }
  };

  const currentCategories =
    article.columnType === 'industry' ? industryCategories : countryCategories;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <button onClick={onCancel} className="text-gray-600 hover:text-gray-700 p-2">
                <i className="ri-arrow-left-line text-xl"></i>
              </button>
              <h1 className="text-xl font-semibold text-gray-900">
                {isEditing ? '编辑文章' : '新建文章'}
              </h1>
            </div>
            <div className="flex items-center space-x-3">
              <button
                onClick={() => setActiveTab(activeTab === 'edit' ? 'preview' : 'edit')}
                className="text-gray-600 hover:text-gray-700 px-3 py-2 rounded-lg border"
              >
                {activeTab === 'edit' ? '预览' : '编辑'}
              </button>
              <button onClick={onCancel} className="text-gray-600 hover:text-gray-700 px-4 py-2 rounded-lg border">
                取消
              </button>
              <button onClick={onSave} className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors">
                保存
              </button>
              <button onClick={onLogout} className="text-gray-600 hover:text-gray-700 px-3 py-2 rounded-lg border" title="退出登录">
                <i className="ri-logout-box-line"></i>
              </button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 py-6">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* 文章设置侧边栏 */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg shadow-sm p-6 space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">发布状态</label>
                <select
                  value={article.status}
                  onChange={(e) =>
                    handleInputChange('status', e.target.value as 'draft' | 'published')
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="draft">草稿</option>
                  <option value="published">已发布</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">发布日期</label>
                <input
                  type="date"
                  value={article.date}
                  onChange={(e) => handleInputChange('date', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">栏目类型</label>
                <select
                  value={article.columnType}
                  onChange={(e) =>
                    handleColumnTypeChange(e.target.value as 'industry' | 'country')
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-5"
                >
                  <option value="industry">行业栏目</option>
                  <option value="country">国家栏目</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {article.columnType === 'industry' ? '行业分类' : '国家分类'}
                </label>
                <select
                  value={article.category}
                  onChange={(e) => handleInputChange('category', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  {currentCategories.map((category) => (
                    <option key={category} value={category}>
                      {category}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">原文链接</label>
                <div className="space-y-2">
                  <input
                    type="url"
                    value={article.link}
                    onChange={(e) => handleInputChange('link', e.target.value)}
                    placeholder="https://mp.weixin.qq.com/s/..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                  <button
                    onClick={extractContentFromUrl}
                    disabled={isExtracting || !article.link.trim()}
                    className="w-full px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center justify-center"
                  >
                    {isExtracting ? (
                      <>
                        <i className="ri-loader-4-line animate-spin mr-2"></i>
                        抓取中...
                      </>
                    ) : (
                      <>
                        <i className="ri-download-cloud-line mr-2"></i>
                        智能抓取内容
                      </>
                    )}
                  </button>
                  <p className="text-xs text-gray-500">
                    💡 输入文章链接后点击"智能抓取内容"，系统将自动提取标题、内容、图片等信息
                  </p>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">封面图片</label>
                <div className="space-y-3">
                  {/* 图片预览 */}
                  {article.image && (
                    <div className="relative">
                      <img
                        src={article.image}
                        alt="封面图片预览"
                        className="w-full h-48 object-cover rounded-lg border border-gray-300"
                      />
                      <button
                        onClick={() => setArticle({ ...article, image: '' })}
                        className="absolute top-2 right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center hover:bg-red-600 transition-colors"
                        title="删除图片"
                      >
                        <i className="ri-close-line text-sm"></i>
                      </button>
                    </div>
                  )}

                  {/* 上传/链接 */}
                  <div className="flex flex-col space-y-2">
                    <button
                      onClick={triggerCoverImageUpload}
                      disabled={coverImageUploading}
                      className="w-full px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center justify-center"
                    >
                      {coverImageUploading ? (
                        <>
                          <i className="ri-loader-4-line animate-spin mr-2"></i>
                          上传中...
                        </>
                      ) : (
                        <>
                          <i className="ri-image-add-line mr-2"></i>
                          {article.image ? '更换封面图片' : '上传封面图片'}
                        </>
                      )}
                    </button>

                    <div className="text-center text-sm text-gray-500">或</div>
                    <input
                      type="url"
                      value={article.image.startsWith('data:') ? '' : article.image}
                      onChange={(e) => handleInputChange('image', e.target.value)}
                      placeholder="输入图片链接地址"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>

                  <input
                    id="cover-image-upload"
                    type="file"
                    accept="image/*"
                    onChange={handleCoverImageUpload}
                    className="hidden"
                  />

                  <p className="text-xs text-gray-500">
                    💡 支持 jpg、png、gif 等格式，建议尺寸 800x400 像素，文件大小不超过 10 MB
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* 主编辑区域 */}
          <div className="lg:col-span-3">
            <div className="bg-white rounded-lg shadow-sm">
              {activeTab === 'edit' ? (
                <div className="p-6 space-y-6">
                  {/* 标题 */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">文章标题</label>
                    <input
                      type="text"
                      value={article.title}
                      onChange={(e) => handleInputChange('title', e.target.value)}
                      placeholder="请输入文章标题"
                      className="w-full px-4 py-3 text-lg border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>

                  {/* 摘要 */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">文章摘要</label>
                    <textarea
                      value={article.summary}
                      onChange={(e) => handleInputChange('summary', e.target.value)}
                      placeholder="请输入文章摘要"
                      rows={3}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>

                  {/* 智能... */}
                  <div className="flex items-center justify-between border-b border-gray-200 pb-4">
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={processContentIntelligently}
                        disabled={isProcessing}
                        className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center"
                      >
                        {isProcessing ? (
                          <>
                            <i className="ri-loader-4-line animate-spin mr-2"></i>
                            智能处理中...
                          </>
                        ) : (
                          <>
                            <i className="ri-magic-line mr-2"></i>
                            智能处理内容
                          </>
                        )}
                      </button>
                    </div>
                    <p className="text-sm text-gray-500">
                      💡 输入内容后点击智能处理，自动优化格式并生成配图
                    </p>
                  </div>

                  {/* 富文本编辑器 */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">文章内容</label>
                    <RichTextEditor
                      value={article.content}
                      onChange={(value) => handleInputChange('content', value)}
                      placeholder="开始编写内容..."
                      className="min-h-[500px]"
                    />
                    <div className="mt-2 text-xs text-gray-500">
                      <span className="text-green-600">
                        ✨ 新功能：现在使用富文本编辑器，支持所见即所得的编辑体验！
                      </span>
                      <br />
                      <span className="text-blue-600">
                        💡 使用工具栏可以快速格式化文本：粗体、斜体、标题、列表等
                      </span>
                      <br />
                      <span className="text-purple-600">
                        🚀 智能功能：输入内容后，点击"智能处理内容"按钮进行自动优化和配图
                      </span>
                      <br />
                      <span className="text-green-600">
                        📝 抓取功能：在左侧输入文章链接，点击"智能抓取内容"可自动获取文章内容
                      </span>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="p-6">
                  <div className="prose max-w-none">
                    <h1 className="text-3xl font-bold mb-4">{article.title || '未命名文章'}</h1>
                    <div className="text-gray-600 mb-6">
                      <span className="mr-4">{article.date}</span>
                      <span
                        className={`px-2 py-1 text-sm rounded mr-2 ${
                          article.columnType === 'industry'
                            ? 'bg-purple-100 text-purple-600'
                            : 'bg-orange-100 text-orange-600'
                        }`}
                      >
                        {article.columnType === 'industry' ? '行业栏目' : '国家栏目'}
                      </span>
                      <span
                        className={`px-2 py-1 text-sm rounded ${
                          article.columnType === 'industry'
                            ? 'bg-blue-100 text-blue-600'
                            : 'bg-green-100 text-green-600'
                        }`}
                      >
                        {article.category}
                      </span>
                    </div>
                    {article.summary && (
                      <div className="bg-blue-50 p-4 rounded-lg mb-6">
                        <p className="text-blue-800">{article.summary}</p>
                      </div>
                    )}
                    {article.link && (
                      <div className="bg-gray-50 p-4 rounded-lg mb-6">
                        <p className="text-sm text-gray-600 mb-2">原文链接：</p>
                        <a
                          href={article.link}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-600 hover:text-blue-700 underline break-all"
                        >
                          {article.link}
                        </a>
                      </div>
                    )}
                    <div
                      className="prose max-w-none"
                      dangerouslySetInnerHTML={{ __html: article.content }}
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminPanel;
